import React, { useContext, useState, useEffect } from "react";
import { UserContext, API } from "@/App";
import { getAnimal, getRank, getNextRank, getNextMilestone, getRandomEncouragement, MILESTONES, RANKS, GOLD_COIN_URL, TREASURE_ICONS } from "@/data/gameData";
import { Download, Compass, Calendar, ArrowRight, Users, X, Mail, Target, HelpCircle, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StreaksCalendar from "@/components/StreaksCalendar";
import axios from "axios";
import { toast } from "sonner";

function TreasureVisual({ milestone }) {
  if (!milestone) return null;
  const bgColors = {
    "Diamond Ring": "#FEE2E2", "Silver Ring": "#F3F4F6", "Pearl Necklace": "#FFF7ED",
    "Diamond Crown": "#DBEAFE", "Gold Goblet": "#FEF3C7", "Jeweled Sword": "#EDE9FE",
    "Pile of 5 Diamonds": "#CFFAFE", "Golden Crown": "#FEF3C7", "The Legendary Treasure": "#FEF3C7",
  };
  const icon = TREASURE_ICONS[milestone.reward] || milestone.icon;
  return (
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 relative overflow-hidden"
      style={{ background: bgColors[milestone.reward] || "#FEF3C7", border: `3px solid ${milestone.color || "#D97706"}` }}>
      <span className="text-3xl relative z-10">{icon}</span>
    </div>
  );
}

export default function HomeDashboard({ onNavigate, stats, refreshStats, onLogout }) {
  const { currentUser } = useContext(UserContext);
  const [showChest, setShowChest] = useState(false);
  const [showRanks, setShowRanks] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [goalCount, setGoalCount] = useState(stats?.personal_goal_count || 5);
  const [goalFreq, setGoalFreq] = useState(stats?.personal_goal_frequency || "month");
  const [showNote, setShowNote] = useState(null);
  const [riddle, setRiddle] = useState(null);
  const [riddleRevealed, setRiddleRevealed] = useState(false);

  // Load encouragement notes
  useEffect(() => {
    if (currentUser) {
      axios.get(`${API}/users/${currentUser.id}/encouragement`).then(r => {
        setNotes(r.data || []);
      }).catch(() => {});
    }
  }, [currentUser]);

  // Load riddle of the day
  useEffect(() => {
    axios.get(`${API}/riddle-of-the-day`).then(r => { if (r.data) setRiddle(r.data); }).catch(() => {});
  }, []);

  const unreadNotes = notes.filter(n => !n.read);

  const markNoteRead = (note) => {
    axios.put(`${API}/users/${currentUser.id}/encouragement/${note.id}/read`).catch(() => {});
    setNotes(prev => prev.map(n => n.id === note.id ? { ...n, read: true } : n));
    setShowNote(null);
  };

  // Sync goal state when stats load
  useEffect(() => {
    if (stats?.personal_goal_count) setGoalCount(stats.personal_goal_count);
    if (stats?.personal_goal_frequency) setGoalFreq(stats.personal_goal_frequency);
  }, [stats?.personal_goal_count, stats?.personal_goal_frequency]);

  const saveGoal = async () => {
    try {
      await axios.put(`${API}/users/${currentUser.id}`, {
        personal_goal_count: goalCount,
        personal_goal_frequency: goalFreq
      });
      toast.success("Goal updated!");
      setShowGoalEdit(false);
      if (refreshStats) refreshStats();
    } catch { toast.error("Failed to save goal"); }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/users/${currentUser.id}/export/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${currentUser.name}_TreasureTome.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Collection downloaded!");
    } catch { toast.error("Failed to download"); }
  };

  if (!stats) return <div className="text-center py-20 font-heading" style={{ color: "#7A5946" }}>Loading your adventure...</div>;

  const animal = getAnimal(stats.avatar_animal);
  const rank = stats.rank || getRank(stats.memorized);
  const nextRank = stats.next_rank || getNextRank(stats.memorized);
  const nextMilestone = stats.next_milestone || getNextMilestone(stats.streak_days);
  const goalSeconds = stats.daily_goal_minutes * 60;
  const practiceProgress = Math.min((stats.practice_time_today_seconds / goalSeconds) * 100, 100);
  const practiceMinutes = Math.floor(stats.practice_time_today_seconds / 60);
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (practiceProgress / 100) * circumference;
  const earnedTreasures = stats.rewards || [];

  return (
    <div className="slide-up" data-testid="home-dashboard">
      {/* Encouragement Banner */}
      {unreadNotes.length > 0 && (
        <button onClick={() => setShowNote(unreadNotes[0])}
          className="tt-card w-full p-3 mb-4 flex items-center gap-3 text-left"
          style={{ background: "linear-gradient(135deg, #FFE4E6, #FFF1F2)", border: "3px solid #FB7185" }}
          data-testid="encouragement-banner">
          <Mail className="w-6 h-6 flex-shrink-0" style={{ color: "#FB7185" }} />
          <div className="flex-1">
            <p className="font-heading text-sm font-bold" style={{ color: "#4A3224" }}>You have a message!</p>
            <p className="text-xs" style={{ color: "#7A5946" }}>From {unreadNotes[0].from_name}</p>
          </div>
          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ background: "#FB7185" }}>{unreadNotes.length}</div>
        </button>
      )}

      {/* Encouragement Note Dialog */}
      {showNote && (
        <Dialog open={!!showNote} onOpenChange={(o) => { if (!o) markNoteRead(showNote); }}>
          <DialogContent className="sm:max-w-[360px] p-6 rounded-2xl text-center" data-testid="note-dialog">
            <DialogTitle className="sr-only">Encouragement</DialogTitle>
            <DialogDescription className="sr-only">A message for you</DialogDescription>
            <span className="text-4xl block mb-3">💌</span>
            <p className="font-heading text-xl font-bold mb-2" style={{ color: "#4A3224" }}>
              A message from {showNote.from_name}!
            </p>
            <div className="parchment rounded-xl p-4 mb-4">
              <p className="text-base italic" style={{ color: "#4A3224" }}>"{showNote.message}"</p>
            </div>
            <button onClick={() => markNoteRead(showNote)} className="tt-btn tt-btn-coral px-6 py-2"
              data-testid="close-note-btn">Thanks!</button>
          </DialogContent>
        </Dialog>
      )}

      {/* HERO */}
      <div className="open-book p-6 mb-6" data-testid="hero-section">
        <div className="flex items-center gap-2 mb-3">
          <img src="https://customer-assets.emergentagent.com/job_seven-seas-poetry/artifacts/br7pcwk1_IMG_8782.jpeg"
            alt="Treasure Tome" className="w-24 h-24 rounded-full object-cover" />
          <h2 className="font-heading text-xl sm:text-2xl font-bold" style={{ color: "#4A3224" }}>
            Welcome back, {currentUser?.name}!
          </h2>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "#7A5946" }}>
          Head to the <strong style={{ color: "#4A3224" }}>Library</strong> to discover poems, scriptures, and songs to add to your collection — every item you memorize earns you gold coins for the store! Then visit <strong style={{ color: "#4A3224" }}>My Tome</strong> to practice and review what you've learned — that's how you deepen your memory and climb the ranks.
        </p>
      </div>

      {/* Riddle of the Day */}
      {riddle && (
        <div className="tt-card p-4 mb-5" data-testid="riddle-of-the-day">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="w-4 h-4" style={{ color: "#D97706" }} />
            <p className="font-heading text-sm font-bold" style={{ color: "#4A3224" }}>Riddle of the Day</p>
          </div>
          <div className="parchment rounded-xl p-3">
            <p className="whitespace-pre-wrap text-sm leading-6" style={{ fontFamily: "'Nunito', sans-serif", color: "#4A3224" }}>
              {riddle.content?.split('\n').filter(line => !line.startsWith('[')).join('\n')}
            </p>
            {(() => {
              const answerMatch = riddle.content?.match(/\[([^\]]+)\]/);
              if (!answerMatch) return null;
              return (
                <button onClick={() => setRiddleRevealed(!riddleRevealed)}
                  className="flex items-center gap-1.5 mt-2 text-xs font-heading font-semibold"
                  style={{ color: "#D97706" }}
                  data-testid="riddle-reveal-btn">
                  {riddleRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {riddleRevealed ? "Hide Answer" : "Reveal Answer"}
                </button>
              );
            })()}
            {riddleRevealed && (() => {
              const answerMatch = riddle.content?.match(/\[([^\]]+)\]/);
              return answerMatch ? (
                <p className="mt-1 text-sm font-heading font-bold" style={{ color: "#16A34A" }} data-testid="riddle-answer">
                  {answerMatch[1]}
                </p>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Avatar + Timer */}
      <div className="flex items-center gap-5 mb-5">
        <div className="relative flex-shrink-0">
          <svg width="136" height="136" className="timer-ring">
            <circle cx="68" cy="68" r={radius} fill="none" stroke="#E8DDD0" strokeWidth="7" />
            <circle cx="68" cy="68" r={radius} fill="none" stroke="#FFC107" strokeWidth="7"
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 0.5s ease" }} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[88px] h-[88px] rounded-full overflow-hidden"
              style={{ border: `3px solid ${animal.color}`, background: `${animal.color}15` }}>
              {animal.image ? (
                <img src={animal.image} alt={animal.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl" style={{ background: `${animal.color}20` }}>{animal.emoji}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex-1">
          <p className="font-heading text-lg font-bold" style={{ color: "#4A3224" }}>{getRandomEncouragement()}</p>
          <p className="text-xs mt-1" style={{ color: "#7A5946" }}>{practiceMinutes} / {stats.daily_goal_minutes} min today</p>
          <div className="w-full h-2 rounded-full mt-2" style={{ background: "#E8DDD0" }}>
            <div className="h-full rounded-full transition-all" style={{
              width: `${practiceProgress}%`, background: "linear-gradient(90deg, #FFC107, #F59E0B)"
            }} />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="tt-card p-3 text-center" data-testid="gold-coins-stat">
          <img src={GOLD_COIN_URL} alt="coin" className="w-8 h-8 mx-auto mb-1 object-contain rounded-full" />
          <p className="font-heading text-xl font-bold" style={{ color: "#D97706" }}>{stats.gold_coins}</p>
          <p className="text-[10px] font-semibold" style={{ color: "#7A5946" }}>Gold Coins</p>
        </div>
        <div className="tt-card p-3 text-center" data-testid="streak-stat">
          <span className="text-2xl block mb-1">🔥</span>
          <p className="font-heading text-xl font-bold" style={{ color: "#FB7185" }}>{stats.streak_days}</p>
          <p className="text-[10px] font-semibold" style={{ color: "#7A5946" }}>Day Streak</p>
        </div>
        <div className="tt-card p-3 text-center" data-testid="memorized-stat">
          <span className="text-2xl block mb-1">📚</span>
          <p className="font-heading text-xl font-bold" style={{ color: "#38BDF8" }}>{stats.memorized}</p>
          <p className="text-[10px] font-semibold" style={{ color: "#7A5946" }}>Memorized</p>
        </div>
      </div>

      {/* Personal Goal Progress */}
      {(() => {
        const goalTarget = stats.personal_goal_count || 5;
        const goalPeriod = stats.personal_goal_frequency || "month";
        const goalProgress = stats.goal_progress || 0;
        const goalPct = Math.min((goalProgress / goalTarget) * 100, 100);
        const goalReached = goalProgress >= goalTarget;
        const freqLabel = { day: "today", week: "this week", month: "this month", year: "this year" }[goalPeriod] || goalPeriod;
        return (
          <button onClick={() => setShowGoalEdit(true)} className="tt-card p-4 mb-3 w-full text-left hover:scale-[1.01] transition-transform"
            data-testid="goal-btn">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4" style={{ color: goalReached ? "#16A34A" : "#7C3AED" }} />
                <p className="font-heading text-sm font-semibold" style={{ color: "#4A3224" }}>
                  My Goal: {goalTarget} item{goalTarget !== 1 ? "s" : ""} / {goalPeriod}
                </p>
              </div>
              {goalReached && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#16A34A" }}>Done!</span>}
            </div>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: "#E8DDD0" }}>
              <div className="h-full rounded-full transition-all" style={{
                width: `${goalPct}%`,
                background: goalReached ? "linear-gradient(90deg, #16A34A, #22C55E)" : "linear-gradient(90deg, #7C3AED, #A78BFA)"
              }} />
            </div>
            <p className="text-xs mt-1" style={{ color: "#7A5946" }}>{goalProgress} / {goalTarget} memorized {freqLabel}</p>
          </button>
        );
      })()}

      {/* Goal Edit Dialog */}
      <Dialog open={showGoalEdit} onOpenChange={setShowGoalEdit}>
        <DialogContent className="sm:max-w-[400px] p-6 rounded-2xl" data-testid="goal-edit-dialog">
          <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Set Your Goal</DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "#7A5946" }}>
            How many items do you want to memorize? Set a goal that feels right for you — and remember, reviewing what you've learned is what helps you climb the ranks!
          </DialogDescription>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{ color: "#7A5946" }}>Items</label>
              <Select value={String(goalCount)} onValueChange={v => setGoalCount(parseInt(v))}>
                <SelectTrigger className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="goal-count-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{ color: "#7A5946" }}>Per</label>
              <Select value={goalFreq} onValueChange={setGoalFreq}>
                <SelectTrigger className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="goal-freq-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <button onClick={saveGoal} className="tt-btn tt-btn-ocean w-full py-3 mt-4" data-testid="save-goal-btn">
            Save Goal
          </button>
        </DialogContent>
      </Dialog>

      {/* Clickable Rank Progress */}
      <button onClick={() => setShowRanks(true)} className="tt-card p-4 mb-4 w-full text-left hover:scale-[1.01] transition-transform"
        data-testid="rank-btn">
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading text-sm font-semibold" style={{ color: "#4A3224" }}>Rank: {rank.name} {rank.icon}</p>
          <div className="flex items-center gap-1">
            {nextRank && <p className="text-xs font-heading font-semibold" style={{ color: "#D97706" }}>Next: {nextRank.name}</p>}
            <ArrowRight className="w-4 h-4" style={{ color: "#D8C3B5" }} />
          </div>
        </div>
        {nextRank && (
          <>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: "#E8DDD0" }}>
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(((stats.tier3_plus || 0) / nextRank.threshold) * 100, 100)}%`,
                background: "linear-gradient(90deg, #FFC107, #F59E0B)"
              }} />
            </div>
            <p className="text-xs mt-1" style={{ color: "#7A5946" }}>{stats.tier3_plus || 0} / {nextRank.threshold} deeply retained</p>
          </>
        )}
      </button>

      {/* Rank Ladder Dialog */}
      <Dialog open={showRanks} onOpenChange={setShowRanks}>
        <DialogContent className="sm:max-w-[450px] p-0 rounded-2xl overflow-y-auto" style={{ maxHeight: "80vh" }}
          data-testid="rank-ladder-dialog">
          <DialogTitle className="sr-only">Rank Ladder</DialogTitle>
          <DialogDescription className="sr-only">All ranks and how to achieve them</DialogDescription>
          <div className="p-6">
            <h2 className="font-heading text-xl font-bold mb-1" style={{ color: "#4A3224" }}>Rank Ladder</h2>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>Review your poems to deepen your memory and climb the ranks! Ranks are based on poems retained at Tier 3 or higher.</p>
            <div className="space-y-3">
              {RANKS.map((r, i) => {
                const isAchieved = (stats.tier3_plus || 0) >= r.threshold;
                const isCurrent = rank.rank === r.rank;
                return (
                  <div key={r.rank} className={`p-3 rounded-xl flex items-center gap-3 ${isCurrent ? "ring-2 ring-amber-400" : ""}`}
                    style={{ background: isAchieved ? "#FEF3C7" : "#F5F0E8", border: `2px solid ${isAchieved ? "#FFC107" : "#E8DDD0"}` }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                      style={{ background: isAchieved ? "#FFC107" : "#D8C3B5", color: isAchieved ? "#5C3D2E" : "#A0896C" }}>
                      {r.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-heading text-sm font-bold" style={{ color: isAchieved ? "#4A3224" : "#A0896C" }}>
                        {r.name} {isCurrent && <span className="text-[10px] font-normal" style={{ color: "#D97706" }}>(You!)</span>}
                      </p>
                      <p className="text-[10px]" style={{ color: "#7A5946" }}>
                        {r.threshold === 0 ? "Starting rank" : `${r.threshold} poems deeply retained`} · {r.location}
                      </p>
                    </div>
                    {isAchieved && <span className="text-lg">✅</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Next Treasure - clickable */}
      <button onClick={() => setShowChest(true)}
        className="tt-card p-4 mb-5 flex items-center gap-4 w-full text-left hover:scale-[1.01] transition-transform"
        data-testid="next-milestone-btn">
        {nextMilestone ? (
          <>
            <TreasureVisual milestone={nextMilestone} />
            <div className="flex-1">
              <p className="font-heading text-sm font-bold" style={{ color: "#4A3224" }}>Next Treasure</p>
              <p className="font-heading text-base font-bold" style={{ color: "#D97706" }}>{nextMilestone.reward}</p>
              <p className="text-xs" style={{ color: "#7A5946" }}>
                {nextMilestone.count - stats.streak_days} more streak days to unlock!
              </p>
            </div>
            <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: "#D8C3B5" }} />
          </>
        ) : (
          <>
            <span className="text-4xl">🏆</span>
            <div className="flex-1">
              <p className="font-heading text-base font-bold" style={{ color: "#D97706" }}>All Treasures Collected!</p>
              <p className="text-xs" style={{ color: "#7A5946" }}>Tap to view your treasure chest</p>
            </div>
            <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: "#D8C3B5" }} />
          </>
        )}
      </button>

      {/* Treasure Chest Dialog */}
      <Dialog open={showChest} onOpenChange={setShowChest}>
        <DialogContent className="sm:max-w-[500px] p-0 rounded-2xl overflow-y-auto" style={{ maxHeight: "80vh" }}
          data-testid="treasure-chest-dialog">
          <DialogTitle className="sr-only">Treasure Chest</DialogTitle>
          <DialogDescription className="sr-only">Your collected treasures</DialogDescription>
          <div className="p-6">
            <h2 className="font-heading text-xl font-bold mb-1" style={{ color: "#4A3224" }}>Your Treasure Chest</h2>
            <p className="text-sm mb-4" style={{ color: "#7A5946" }}>Earned through your daily streak!</p>
            {earnedTreasures.length === 0 ? (
              <div className="parchment rounded-xl p-8 text-center">
                <span className="text-5xl block mb-3">🪙</span>
                <p className="font-heading text-base" style={{ color: "#7A5946" }}>Keep your streak going to unlock treasures!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {earnedTreasures.map((r, i) => (
                  <div key={i} className="tt-card p-3 text-center">
                    <span className="text-3xl block mb-1">{TREASURE_ICONS[r.type] || "🎁"}</span>
                    <p className="font-heading text-xs font-semibold" style={{ color: "#4A3224" }}>{r.type}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-3" style={{ borderTop: "1px dashed #D8C3B5" }}>
              <p className="text-xs font-semibold mb-2" style={{ color: "#7A5946" }}>Upcoming:</p>
              {MILESTONES.filter(m => m.count > stats.streak_days).slice(0, 3).map((m, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{TREASURE_ICONS[m.reward] || "🎁"}</span>
                  <span className="text-xs" style={{ color: "#7A5946" }}>{m.reward} — {m.count} day streak</span>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Streaks Calendar */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4" style={{ color: "#D97706" }} />
          <h3 className="font-heading text-base font-semibold" style={{ color: "#4A3224" }}>Practice Streaks</h3>
        </div>
        <StreaksCalendar userId={currentUser?.id} streakDays={stats?.streak_days || 0} />
      </div>

      {/* Bottom links */}
      <div className="flex flex-col items-center gap-2 mb-4">
        <button onClick={handleExportPDF}
          className="text-xs flex items-center gap-1 opacity-50 hover:opacity-90 transition-opacity"
          style={{ color: "#7A5946" }} data-testid="download-collection-btn">
          <Download className="w-3 h-3" /> Download my collection as PDF
        </button>
      </div>

      {/* Switch User Bar - full width, stands out */}
      <button onClick={onLogout}
        className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-heading text-sm font-semibold transition-transform hover:scale-[1.01]"
        style={{ background: "linear-gradient(90deg, #8C5E3C, #A0744E)", color: "#FFF9F0", border: "2px solid #5C3D2E" }}
        data-testid="switch-profile-btn">
        <Users className="w-4 h-4" /> Switch User
      </button>
    </div>
  );
}
